import * as React from 'react';
import type { Editor } from '@tiptap/react';

import {
  useIsArtifactFocusView,
  usePanelUnifiedChrome,
} from '@cotera/client/v0/chat/artifact-panel-context';
import { cn } from '@cotera/client/v0/lib/utils';
import type { CommentAuthor } from '../../../../ui/tiptap/extensions/comment-mark';
import {
  CommentMarginCards,
  MARGIN_CARD_GAP_PX,
  MARGIN_CARD_WIDTH_PX,
} from './comment-margin-cards';
import { useCommentThreads } from './comment-threads';
import { useCommentsRail } from './comments-rail-provider';

/** The strip held for the comment column: the card, and air on both sides of it. */
const MARGIN_RESERVE_PX = MARGIN_CARD_WIDTH_PX + MARGIN_CARD_GAP_PX * 2;

/**
 * The least room the text needs beside the reserved strip.
 *
 * Below this the pane is not a document with notes in the margin, it is two columns
 * fighting — and the margin counts, with the rail one click away, are the better
 * answer. In practice this is the artifact panel beside a chat.
 */
const PROSE_KEEP_WIDTH_PX = 400;

const CommentMarginModeContext = React.createContext<'cards' | 'chips'>(
  'chips'
);

/**
 * Whether the document is showing its comments as cards in the margin.
 *
 * The margin chips read this and stand down when it is `cards`: a count pointing at a
 * card already open beside the same line is a label on a thing that is showing.
 */
export function useCommentMarginMode(): 'cards' | 'chips' {
  return React.useContext(CommentMarginModeContext);
}

export interface CommentMarginHostProps {
  editor: Editor | null;
  author: CommentAuthor | null;
  className?: string;
  children: React.ReactNode;
}

/**
 * The editor's scroll container, with a column held for its comments.
 *
 * The strip on the right is reserved, at one fixed width, and the text takes what is
 * left — centred in it by `RESOURCE_MEASURE`, as it always was. Opening the sidebar,
 * or any other change to the pane, comes out of the text's share: the comment column
 * neither moves nor resizes.
 *
 * That fixed reserve is the whole point, and it is worth saying why, because two
 * earlier attempts here were more clever and both were worse. Sizing the cards to
 * whatever margin a centred column happened to leave meant they shrank, grew and
 * finally vanished as the window changed around them — the conversation getting
 * quieter the busier the screen got. Reserving the column instead means a comment
 * looks the same at every width the document is readable at, and the one thing that
 * gives way is the thing that has the most to give.
 *
 * Nothing is reserved until there is something to put in it. A document with no open
 * comments lays out exactly as it did before this feature existed — an empty strip
 * held against the possibility of a comment is a margin nobody asked for on the great
 * majority of documents, which have none.
 *
 * And only where a document is the whole surface: its own page, or the artifact focus
 * view. In the artifact panel beside a chat the document is already one column of a
 * split, and a second column inside it is a split within a split however wide the
 * reader drags it — that pane keeps the margin counts, with the index one click away.
 */
export const CommentMarginHost: React.FC<CommentMarginHostProps> = ({
  editor,
  author,
  className,
  children,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [width, setWidth] = React.useState(0);
  // Where the text column's right edge is, measured from this container's left.
  const [proseRight, setProseRight] = React.useState(0);
  const { counts } = useCommentThreads(editor);
  // Inside the chat's artifact panel — which the focus view technically also is,
  // hence the second question.
  const inArtifactPanel = usePanelUnifiedChrome() !== null;
  const isFocusView = useIsArtifactFocusView();
  const rail = useCommentsRail();

  React.useEffect(() => {
    const container = containerRef.current;
    if (container === null) {
      return;
    }
    const measure = () => {
      const containerRect = container.getBoundingClientRect();
      // The *border* box. Reserving the strip puts `paddingRight` on this very
      // element, which shrinks its content box — read that instead and the reserve
      // would take itself back below the threshold, drop, clear it again, and go
      // round forever. The border box is set by the pane outside and does not move
      // when the padding does.
      setWidth((previous) =>
        previous === containerRect.width ? previous : containerRect.width
      );

      const prose = editor?.view.dom ?? null;
      if (prose === null) {
        return;
      }
      const edge = prose.getBoundingClientRect().right - containerRect.left;
      setProseRight((previous) => (previous === edge ? previous : edge));
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    const prose = editor?.view.dom;
    if (prose !== undefined) {
      observer.observe(prose);
    }
    return () => observer.disconnect();
  }, [editor]);

  const showCards =
    author !== null &&
    counts.open > 0 &&
    // Not while the index is open. It is showing the same conversations in a column
    // of its own, and two sets of the same cards either side of the text is the
    // document reading like a corridor.
    rail?.open !== true &&
    (isFocusView || !inArtifactPanel) &&
    width >= MARGIN_RESERVE_PX + PROSE_KEEP_WIDTH_PX;

  return (
    <CommentMarginModeContext.Provider value={showCards ? 'cards' : 'chips'}>
      <div
        ref={containerRef}
        // The cards are absolutely positioned against this box and scroll with the
        // prose because they live inside the scrolling element, not over it.
        className={cn('relative h-full min-h-0 overflow-y-auto', className)}
        style={showCards ? { paddingRight: MARGIN_RESERVE_PX } : undefined}
      >
        {children}
        {showCards ? (
          <CommentMarginCards
            editor={editor}
            author={author}
            containerRef={containerRef}
            // Beside the text, not against the window. The reserve shifts the reading
            // column left to make this room, and the cards belong at the edge of that
            // column — pinned to the container instead, they drifted to the far right
            // of a wide pane and left a few hundred pixels of nothing between a
            // comment and the line it is about. Together the two read as one block
            // centred on the page, which is the whole point of reserving the strip.
            left={proseRight + MARGIN_CARD_GAP_PX}
          />
        ) : null}
      </div>
    </CommentMarginModeContext.Provider>
  );
};
